import {
  ESdChangeReason,
  ESymbolCategory,
  ICustomBlockUsedStyles,
  IDisplayItem,
  IFillStyle,
  IProject,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import jsonStringifySafe from "json-stringify-safe";
import _ from "lodash";
import ObjectHash from "object-hash";
import { BlockModel, BlocksService } from "../../services/blocks-service";
import { LogService } from "../../services/log-service";
import { PartialContextProvider } from "../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { IScreenDesignContextState } from "./screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "./screen-design-context-provider";

export interface CustomBlocksProviderState {
  blocks: BlockModel[];
  isBlocksLoading: boolean;
  projectToBeSavedForCustomBlockStyles?: IProject;
  isSavingBlockAsCustomBlockModalVisible: boolean;
  selectedKuikaIdForCustomBlock: string | null;
  handleOpenSaveBlockAsCustomBlockModal: (selectedKuikaId: string) => void;
  handleCloseSaveBlockAsCustomBlockModal: () => void;
  getCustomBlockAndUsedStyles: () => { di: IDisplayItem; usedStyles?: ICustomBlockUsedStyles } | undefined;
  initializeCustomBlockStyles: (
    customBlockName: string,
    usedStyles: ICustomBlockUsedStyles,
    serializedDisplayItem: string
  ) => string;
  updateProjectForCustomBlockStyles: () => Promise<void>;
  loadBlocks: () => Promise<void>;
  deleteBlock: (blockId: string) => Promise<void>;
}

export class CustomBlocksProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  CustomBlocksProviderState
> {
  static defaultState = {
    blocks: [],
    isBlocksLoading: false,
    projectToBeSavedForCustomBlockStyles: undefined,
    isSavingBlockAsCustomBlockModalVisible: false,
    selectedKuikaIdForCustomBlock: null,
    handleOpenSaveBlockAsCustomBlockModal: () => {},
    handleCloseSaveBlockAsCustomBlockModal: () => {},
    getCustomBlockAndUsedStyles: () => undefined,
    initializeCustomBlockStyles: () => "",
    updateProjectForCustomBlockStyles: () => Promise.resolve(),
    loadBlocks: () => Promise.resolve(),
    deleteBlock: (blockId: string) => Promise.resolve()
  };

  getProviderValues = (): CustomBlocksProviderState => {
    return {
      blocks: this.state.blocks,
      isBlocksLoading: this.state.isBlocksLoading,
      projectToBeSavedForCustomBlockStyles: this.state.projectToBeSavedForCustomBlockStyles,
      isSavingBlockAsCustomBlockModalVisible: this.state.isSavingBlockAsCustomBlockModalVisible,
      selectedKuikaIdForCustomBlock: this.state.selectedKuikaIdForCustomBlock,
      handleOpenSaveBlockAsCustomBlockModal: this.handleOpenSaveBlockAsCustomBlockModal,
      handleCloseSaveBlockAsCustomBlockModal: this.handleCloseSaveBlockAsCustomBlockModal,
      getCustomBlockAndUsedStyles: this.getCustomBlockAndUsedStyles,
      initializeCustomBlockStyles: this.initializeCustomBlockStyles,
      updateProjectForCustomBlockStyles: this.updateProjectForCustomBlockStyles,
      loadBlocks: this.loadBlocks,
      deleteBlock: this.deleteBlock
    };
  };

  handleOpenSaveBlockAsCustomBlockModal = (selectedKuikaId: string) => {
    this.setState({
      isSavingBlockAsCustomBlockModalVisible: true,
      selectedKuikaIdForCustomBlock: selectedKuikaId
    });
  };

  handleCloseSaveBlockAsCustomBlockModal = () => {
    this.setState({
      isSavingBlockAsCustomBlockModalVisible: false,
      selectedKuikaIdForCustomBlock: null
    });
  };

  getCustomBlockAndUsedStyles = (): {
    di: IDisplayItem;
    usedStyles: ICustomBlockUsedStyles;
  } => {
    if (!this.state.selectedKuikaIdForCustomBlock) {
      throw new Error("Element is not found for custom block. Please contact to the customer service");
    }
    const dict = this.partial.screenProvider.getScreenDesignDictionary();

    const parentItem = dict[this.state.selectedKuikaIdForCustomBlock];

    if (!parentItem) throw new Error("Element is not found for custom block. Please contact to the customer service");

    const usedStyles: ICustomBlockUsedStyles = {
      border: [],
      shadow: [],
      fill: [],
      position: [],
      text: [],
      effects: []
    };

    const prepareCustomBlockItem = (item: IDisplayItem): IDisplayItem => {
      const modifiedDisplayItem = _.cloneDeep(item);

      if (modifiedDisplayItem.children && Array.isArray(modifiedDisplayItem.children)) {
        modifiedDisplayItem.children = modifiedDisplayItem.children.map((child) => prepareCustomBlockItem(child));
      }
      modifiedDisplayItem.styleObj?.border?.borderIds?.forEach((borderId) => {
        const borderStyle = this.partial.projectProvider.state.project.stylingBorder.find(
          (styling) => styling.borderId === borderId
        );
        if (borderStyle) usedStyles.border.push(borderStyle);
      });

      modifiedDisplayItem.styleObj?.shadow?.shadowIds?.forEach((shadowId) => {
        const shadowStyle = this.partial.projectProvider.state.project.stylingShadow.find(
          (styling) => styling.shadowId === shadowId
        );
        if (shadowStyle) usedStyles.shadow.push(shadowStyle);
      });

      modifiedDisplayItem.styleObj?.fill?.fillIds?.forEach((fillId) => {
        const fillStyle = this.partial.projectProvider.state.project.stylingFill.find(
          (styling) => styling.fillId === fillId
        );
        if (fillStyle) usedStyles.fill.push(fillStyle);
      });

      modifiedDisplayItem.styleObj?.position?.positionIds?.forEach((positionId) => {
        const positionStyle = this.partial.projectProvider.state.project.stylingPosition.find(
          (styling) => styling.positionId === positionId
        );
        if (positionStyle) usedStyles.position.push(positionStyle);
      });

      modifiedDisplayItem.styleObj?.text?.typoIds?.forEach((typoId) => {
        const textStyle = this.partial.projectProvider.state.project.stylingTypography.find(
          (styling) => styling.typoId === typoId
        );
        if (textStyle) usedStyles.text.push(textStyle);
      });

      modifiedDisplayItem.styleObj?.effects?.effectsIds?.forEach((effectsId) => {
        const effectsStyle = this.partial.projectProvider.state.project.stylingEffects?.find(
          (styling) => styling.effectsId === effectsId
        );
        if (effectsStyle) usedStyles.effects.push(effectsStyle);
      });
      // Remove all actions
      modifiedDisplayItem.item.events?.forEach((event) => {
        event.actions = [];
      });

      // Normalize all properties to fixed
      modifiedDisplayItem.item.properties.map((diProp) => {
        if (diProp.name === "style" || diProp.name === "class") return diProp;
        if (diProp.symbol.category === ESymbolCategory.fixed || diProp.symbol.category === ESymbolCategory.default) {
          diProp.symbol.actionDefinitionId = undefined;
          diProp.symbol.actionDefinitionObjectPath = undefined;
          diProp.symbol.resultActionId = undefined;
          return diProp;
        }

        let fixedValue = "";

        const componentSetObj = this.partial.projectProvider.state.project.componentSetObj.components.find(
          (comp) => comp.name === modifiedDisplayItem.item.name
        );

        if (!componentSetObj) {
          const errorMessage = `
                  DI [${modifiedDisplayItem.item.name}] is not found while creating custom block\n
                  DI JSON:\n
                  ${jsonStringifySafe(modifiedDisplayItem)}`;

          LogService.Log({
            text: errorMessage,
            level: "error",
            method: "DesignLogs",
            json: jsonStringifySafe(modifiedDisplayItem),
            projectId: this.partial.state.project.id,
            screenDesignId: this.partial.state.sdId
          });
          console.error(errorMessage);
        } else {
          const componentSetPropObj = componentSetObj?.properties.find((compProp) => compProp.name === diProp.name);

          if (componentSetPropObj) {
            fixedValue = componentSetPropObj.symbol.fixedValue;
          }
        }

        diProp.symbol = {
          label: diProp.symbol.label,
          fixedValue: fixedValue,
          category: ESymbolCategory.fixed,
          description: ""
        };

        return diProp;
      });

      return modifiedDisplayItem;
    };

    const di: IDisplayItem = prepareCustomBlockItem(parentItem);

    return { di, usedStyles };
  };

  initializeCustomBlockStyles = (
    customBlockName: string,
    usedStyles: ICustomBlockUsedStyles,
    serializedDisplayItem: string
  ) => {
    const project = _.cloneDeep(this.partial.projectProvider.getProject());

    const customBlockNonExistingStyleInjector = (
      usedStyleList:
        | IStylingBorder[]
        | IStylingShadow[]
        | IFillStyle[]
        | IStylingPosition[]
        | IStylingTypography[]
        | IStylingEffects[],
      projectStyleList:
        | IStylingBorder[]
        | IStylingShadow[]
        | IFillStyle[]
        | IStylingPosition[]
        | IStylingTypography[]
        | IStylingEffects[]
        | undefined,
      idProperty: "borderId" | "shadowId" | "fillId" | "positionId" | "typoId" | "effectsId"
    ) => {
      if (!projectStyleList) return;
      usedStyleList.forEach(
        (
          usedStyle:
            | IStylingTypography
            | IStylingBorder
            | IStylingShadow
            | IFillStyle
            | IStylingPosition
            | IStylingEffects
        ) => {
          const newStyleNamingTemplate = `${customBlockName}_${usedStyle.name}`;

          let isProjectStyleFitsPattern = false;
          const existingProjectStyle = projectStyleList.find((projectStyle) => {
            // 1. Exact match (Same Name, Same Content)
            const isEqual = ObjectHash(projectStyle) === ObjectHash(usedStyle);
            if (isEqual) return true;

            // 2. Pattern Match: Check if the project style uses the custom block naming convention
            // Pattern: {CustomBlockName}_{StyleName} OR {CustomBlockName}_{StyleName}{Index}
            // We use startsWith to catch both "Name" and "Name1", "Name2" etc.
            if (projectStyle.name && projectStyle.name.startsWith(newStyleNamingTemplate)) {
              // Now verify if the CONTENT is the same (ignoring the name difference)
              const tempUsedStyle = {
                ...usedStyle,
                name: projectStyle.name,
                [idProperty]: projectStyle[idProperty]
              };
              if (ObjectHash(projectStyle) === ObjectHash(tempUsedStyle)) {
                isProjectStyleFitsPattern = true;
                return true;
              }
            }

            return false;
          });

          if (existingProjectStyle) {
            if (!isProjectStyleFitsPattern) {
              return;
            }
            console.log("existingProjectStyle", existingProjectStyle);
            console.log("usedStyle", usedStyle);

            serializedDisplayItem = serializedDisplayItem.replaceAll(
              usedStyle[idProperty],
              existingProjectStyle[idProperty]
            );
            return;
          }

          if (!existingProjectStyle) {
            const projectStyleWithSameNameOrId = projectStyleList.find(
              (projectStyle) =>
                projectStyle.name === usedStyle.name ||
                projectStyle.name === newStyleNamingTemplate ||
                projectStyle[idProperty] === usedStyle[idProperty]
            );
            const id = Guid.create().toString();

            if (projectStyleWithSameNameOrId) {
              usedStyle.name = KMainFunctions.defaultUniqueListElementNameGenerator(
                projectStyleList,
                "name",
                newStyleNamingTemplate
              );
              serializedDisplayItem = serializedDisplayItem.replaceAll(usedStyle[idProperty], id);
            }
            usedStyle.styleType = "custom";
            usedStyle[idProperty] = id;

            projectStyleList.push(usedStyle as any);
          }
        }
      );
    };

    customBlockNonExistingStyleInjector(usedStyles.border, project.stylingBorder, "borderId");
    customBlockNonExistingStyleInjector(usedStyles.shadow, project.stylingShadow, "shadowId");
    customBlockNonExistingStyleInjector(usedStyles.fill, project.stylingFill, "fillId");
    customBlockNonExistingStyleInjector(usedStyles.position, project.stylingPosition, "positionId");
    customBlockNonExistingStyleInjector(usedStyles.text, project.stylingTypography, "typoId");
    if (!project.stylingEffects) project.stylingEffects = [];
    customBlockNonExistingStyleInjector(usedStyles.effects, project.stylingEffects, "effectsId");

    this.setState({ projectToBeSavedForCustomBlockStyles: project });
    return serializedDisplayItem;
  };

  updateProjectForCustomBlockStyles = async () => {
    if (!this.state.projectToBeSavedForCustomBlockStyles) return;

    await this.partial.projectProvider.updateProject(
      this.state.projectToBeSavedForCustomBlockStyles,
      true,
      ESdChangeReason.ChangeStyleValue
    );
    this.setState({ projectToBeSavedForCustomBlockStyles: undefined });
  };

  loadBlocks = async () => {
    this.setState({ isBlocksLoading: true });
    try {
      const response = await BlocksService.GetBlocksList();
      const blocks = (response.data.blocks || []) as BlockModel[];
      this.setState({ blocks });
    } catch (error) {
      console.error("Failed to load blocks:", error);
      this.setState({ blocks: [] });
    } finally {
      this.setState({ isBlocksLoading: false });
    }
  };

  deleteBlock = async (blockId: string) => {
    try {
      this.setState({ isBlocksLoading: true });
      await BlocksService.DeleteBlock(blockId);
      this.loadBlocks();
    } catch (error) {
      console.error("Failed to delete block:", error);
    }
  };
}
